import type { Place } from '@/types';

type FlatPlace = {
  place: Place;
  depth: number;
};

// Flatten an inventory's place rows into display order: a depth-first walk
// from the topmost places (no parent) down through children, each tagged
// with its depth so the picker can indent the hierarchy. Siblings are
// ordered by name (then id) for a stable, readable list. Any place whose
// parent isn't in the set is treated as a root, so an orphaned row still
// appears rather than vanishing.
const flattenPlaceTree = (places: Place[]): FlatPlace[] => {
  const byParent = new Map<number | null, Place[]>();
  const ids = new Set(places.map((p) => p.id));
  for (const place of places) {
    const parent =
      place.parentPlace !== null && ids.has(place.parentPlace)
        ? place.parentPlace
        : null;
    const siblings = byParent.get(parent) ?? [];
    siblings.push(place);
    byParent.set(parent, siblings);
  }
  for (const siblings of byParent.values()) {
    siblings.sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
  }
  const out: FlatPlace[] = [];
  const walk = (parent: number | null, depth: number) => {
    for (const place of byParent.get(parent) ?? []) {
      out.push({ place, depth });
      walk(place.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
};

export type { FlatPlace };
export { flattenPlaceTree };
